import type { AnalyticsModule, AutofixProbeResult } from "./analytics";
import type { AutofixProbeEntry, WidgetConfig } from "./api";

// Task D4/D4a -- confirms, from real visitor traffic, that a `merged`
// Autofix fix's target element is actually visible and reachable on the
// page it was supposed to fix. No shared code with anything server-side
// (B1's own detector is pure click-analytics now, nothing left to port --
// see ai-service's app/autofix/detectors/hidden_button.py), so this is a
// from-scratch visibility + coverage check, same spirit as the "hidden or
// covered" problem type it's reporting back on.
//
// Dedup is per visitor per fix (localStorage), not per page load -- once
// a verdict is sent for a fix, this visitor never probes it again, even
// across sessions.

const DEDUP_KEY_PREFIX = "artis-autofix-probed-";
// Gives an SPA route a moment to finish rendering before the very first
// `querySelector` attempt -- same idle-before-acting idea as index.ts's
// own attention triggers, just a fixed delay rather than a real idle
// callback (a probe firing a couple seconds "late" costs nothing; a
// querySelector racing a client-side router's render would produce a
// false not_found).
const IDLE_DELAY_MS = 2_000;
const INTERSECTION_THRESHOLD = 0.5;

function alreadyProbed(fixId: string): boolean {
  try {
    return localStorage.getItem(DEDUP_KEY_PREFIX + fixId) === "1";
  } catch {
    return false;
  }
}

function markProbed(fixId: string): void {
  try {
    localStorage.setItem(DEDUP_KEY_PREFIX + fixId, "1");
  } catch {
    // Private browsing / blocked storage -- this visitor just gets
    // re-probed next time, never a hard failure.
  }
}

/** The Tool's own mapped `path` is a route, not a full URL -- an exact
 * pathname match, same precision the dom_action tool itself was mapped
 * against (no prefix/contains fuzz-matching here; that's Analytics'
 * own, separate page-match logic for funnels/paths, not this). */
function pathMatches(probePath: string): boolean {
  return location.pathname === probePath;
}

function isStyleHidden(el: Element): boolean {
  const style = getComputedStyle(el);
  return style.visibility === "hidden" || parseFloat(style.opacity) === 0;
}

/** True if something else is the topmost thing at the element's own
 * center point -- deliberately no special-case for Artis's own widget
 * bubble (data-artis-widget-root): if the widget's own UI happens to sit
 * on top of the target, that's just as real a "visitor can't click this"
 * problem as a cookie banner would be. */
function isCoveredAtCenter(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  const cx = Math.min(Math.max(rect.left + rect.width / 2, 0), window.innerWidth - 1);
  const cy = Math.min(Math.max(rect.top + rect.height / 2, 0), window.innerHeight - 1);
  const topmost = document.elementFromPoint(cx, cy);
  if (!topmost) return true;
  return topmost !== el && !el.contains(topmost) && !topmost.contains(el);
}

function judge(el: Element): AutofixProbeResult {
  if (isStyleHidden(el)) return "covered";
  return isCoveredAtCenter(el) ? "covered" : "visible";
}

/** Waits until `el` actually scrolls into view before judging it --
 * below-the-fold is not "covered" (nothing wrong with a visitor simply
 * not having scrolled there yet); if they never do in this session,
 * this fix is just never probed this time, not marked covered. */
function probeWhenVisible(
  el: Element,
  fixId: string,
  analytics: AnalyticsModule,
): void {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      analytics.captureAutofixProbe(fixId, judge(el), window.innerWidth);
      markProbed(fixId);
    },
    { threshold: INTERSECTION_THRESHOLD },
  );
  observer.observe(el);
}

function runProbe(probe: AutofixProbeEntry, analytics: AnalyticsModule): void {
  const el = document.querySelector(probe.selector);
  if (!el) {
    // Conclusive right away -- no reason to wait for a scroll event that
    // will never surface an element that isn't in the DOM at all.
    analytics.captureAutofixProbe(probe.fix_id, "not_found", window.innerWidth);
    markProbed(probe.fix_id);
    return;
  }
  probeWhenVisible(el, probe.fix_id, analytics);
}

export function startAutofixProbes(
  config: WidgetConfig,
  analytics: AnalyticsModule | null,
): void {
  if (!analytics) return; // no analytics module for this agent -- nothing to ride on
  const probes = (config.autofix_probes || []).filter(
    (p) => pathMatches(p.path) && !alreadyProbed(p.fix_id),
  );
  if (probes.length === 0) return;

  setTimeout(() => {
    for (const probe of probes) runProbe(probe, analytics);
  }, IDLE_DELAY_MS);
}
